// Org-tier locations list. One row per location, whether or not it's
// currently assigned to a subscriber. Phase 3a: read + create. Row click
// links to /locations/:id.

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, IMPORT_TIMEOUT_MS } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { canAdmin, canOperate } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import ListToolbar from '../components/shared/ListToolbar.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ImportMenu from '../components/shared/ImportMenu.jsx';
import SortableHeader, { useSort } from '../components/shared/SortableHeader.jsx';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import Pagination, { usePagination } from '../components/shared/Pagination.jsx';
import { LocationFormModal } from './LocationDetail.jsx';
import SubscriberCountPill from '../components/locations/SubscriberCountPill.jsx';
import { sharedList } from '../lib/liveQuery.js';

const TYPE_LABEL = { business: 'Business', residential: 'Residential' };

const LOCATION_FILTERS = [
  { key: 'all',         label: 'All' },
  { key: 'assigned',    label: 'Assigned' },
  // Addresses serving more than one customer — the ones where teardown and E911
  // need care, so they're worth being able to list on their own.
  { key: 'shared',      label: 'Shared' },
  { key: 'unassigned',  label: 'Unassigned' },
  { key: 'business',    label: 'Business' },
  { key: 'residential', label: 'Residential' },
];

// How many customers are linked to a location. Prefer the server's count; fall
// back to the array, then to the legacy single-owner field so a stale cached
// response doesn't render every address as empty.
function subCount(r) {
  if (r.subscriberCount != null) return Number(r.subscriberCount);
  if (Array.isArray(r.subscribers)) return r.subscribers.length;
  return (r.subscriber?.id ?? r.subscriberId ?? r.subscriber_id) != null ? 1 : 0;
}

function TypePill({ type }) {
  if (!type) return <span className="text-gray-300">—</span>;
  const label = TYPE_LABEL[type] || type;
  const cls = type === 'business'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

function locationStatusVariant(status) {
  switch (status) {
    case 'active':   return 'active';
    case 'inactive': return 'inactive';
    default:         return 'unknown';
  }
}

// The "Label" column is the street-number-plus-street-name from address_line1
// (e.g. "425 Main St"). Fall back to the row's label field, then to a #id.
function labelFromRow(r) {
  const line1 = r.address_line1 || r.addressLine1;
  if (line1 && line1.trim()) return line1.trim();
  return r.label || `Location ${r.id || r.location_id}`;
}

// The "Address" column is the full address: street line + city, state ZIP.
function tailAddress(r) {
  const line1 = r.address_line1 || r.addressLine1;
  const cityStateZip = [r.city, r.state, r.postal || r.zip].filter(Boolean).join(', ');
  const parts = [line1 && line1.trim(), cityStateZip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export default function LocationsList() {
  const { role } = useAuth();
  const canWrite  = canAdmin(role);
  const canImport = canOperate(role);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Row kebab: explicit status override (locations are never deleted).
  const statusMutation = useMutation({
    mutationFn: ({ id, override }) => api.patch(`/locations/${id}`, { status_override: override }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [importToast, setImportToast] = useState(null);
  const sort = useSort({ col: 'label', dir: 'asc' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['locations', sort.col, sort.dir],
    queryFn: () => api.get('/locations', { sort: sort.col, dir: sort.dir }),
    ...sharedList(),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/locations', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setShowCreate(false);
    },
  });

  const importMutation = useMutation({
    mutationFn: () => api.post('/locations/import', undefined, undefined, { timeoutMs: IMPORT_TIMEOUT_MS }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      const n = res?.locations_upserted ?? 0;
      setImportToast({
        ok: true,
        message: `Imported ${n} location${n === 1 ? '' : 's'} from Commio`
          + (res?.errors?.length ? ` (${res.errors.length} error${res.errors.length === 1 ? '' : 's'})` : ''),
      });
      setTimeout(() => setImportToast(null), 5000);
    },
    onError: (err) => {
      setImportToast({
        ok: false,
        message: err?.body?.error || err?.message || 'Import failed',
      });
      setTimeout(() => setImportToast(null), 5000);
    },
  });

  const allRows = useMemo(() => {
    if (Array.isArray(data))            return data;
    if (Array.isArray(data?.rows))      return data.rows;
    if (Array.isArray(data?.locations)) return data.locations;
    return [];
  }, [data]);

  const [locFilter, setLocFilter] = useState('all');
  const [search, setSearch] = useState('');
  const isAssigned = (r) => subCount(r) > 0;
  const isShared   = (r) => subCount(r) > 1;
  const filterCounts = useMemo(() => ({
    all:         allRows.length,
    assigned:    allRows.filter(isAssigned).length,
    shared:      allRows.filter(isShared).length,
    unassigned:  allRows.filter((r) => !isAssigned(r)).length,
    business:    allRows.filter((r) => r.type === 'business').length,
    residential: allRows.filter((r) => r.type === 'residential').length,
  }), [allRows]);
  const rows = useMemo(() => {
    let out = allRows;
    if (locFilter === 'assigned')    out = out.filter(isAssigned);
    if (locFilter === 'shared')      out = out.filter(isShared);
    if (locFilter === 'unassigned')  out = out.filter((r) => !isAssigned(r));
    if (locFilter === 'business')    out = out.filter((r) => r.type === 'business');
    if (locFilter === 'residential') out = out.filter((r) => r.type === 'residential');
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter((r) => {
        const label = String(r.label || '').toLowerCase();
        const addr  = String(r.addressLine1 || r.address_line1 || '').toLowerCase();
        const city  = String(r.city || '').toLowerCase();
        return label.includes(s) || addr.includes(s) || city.includes(s);
      });
    }
    return out;
  }, [allRows, locFilter, search]);

  const { paged: pagedRows, pagination } = usePagination(rows);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Locations</h1>
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <span className="text-sm text-gray-500">
              {rows.length === allRows.length
                ? `${allRows.length} location${allRows.length === 1 ? '' : 's'}`
                : `${rows.length} of ${allRows.length}`}
            </span>
          )}
          {canImport && (
            <ImportMenu
              pending={importMutation.isPending}
              items={[
                { label: 'Import from Commio', onClick: () => importMutation.mutate() },
              ]}
            />
          )}
          {canWrite && (
            <button
              onClick={() => { setShowCreate(true); createMutation.reset(); }}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              New Location
            </button>
          )}
        </div>
      </div>

      {importToast && (
        <div
          className={`rounded-md border px-4 py-2 text-sm ${
            importToast.ok
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {importToast.message}
        </div>
      )}

      {showCreate && (
        <LocationFormModal
          title="New Location"
          submitLabel="Create"
          onSubmit={(body) => createMutation.mutate(body)}
          onCancel={() => { setShowCreate(false); createMutation.reset(); }}
          isPending={createMutation.isPending}
          error={createMutation.isError ? (createMutation.error?.body?.error || createMutation.error?.message || 'Create failed') : null}
        />
      )}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filter by label, address, or city…"
        filters={LOCATION_FILTERS}
        filterCounts={filterCounts}
        activeFilter={locFilter}
        onFilterChange={setLocFilter}
      />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        {isLoading ? (
          <LoadingSpinner message="Loading locations..." />
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-sm text-gray-500">No locations yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <SortableHeader col="status"     sort={sort} label="Status" />
                <SortableHeader col="type"       sort={sort} label="Type" />
                <SortableHeader col="label"      sort={sort} label="Label" />
                <SortableHeader col="address"    sort={sort} label="Address" />
                <SortableHeader col="subscriber" sort={sort} label="Customers" />
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRows.map((r) => {
                const id = r.id || r.location_id;
                const label = labelFromRow(r);
                const address = tailAddress(r);
                const linked = Array.isArray(r.subscribers) ? r.subscribers : [];
                const nSubs = subCount(r);
                // Kebab links straight through when there's exactly one tenant;
                // with several, the location page is the only sane destination.
                const soloSubId = nSubs === 1
                  ? (linked[0]?.id ?? r.subscriber?.id ?? r.subscriberId ?? r.subscriber_id)
                  : null;
                // effectiveStatus reflects the override + subscriber-inherited
                // state (active/inactive); fall back to the raw column.
                const status = r.effectiveStatus ?? r.effective_status ?? r.status ?? 'active';
                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <StatusBadge status={locationStatusVariant(status)} label={status} />
                    </td>
                    <td className="px-4 py-2">
                      <TypePill type={r.type} />
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/locations/${id}`} className="text-blue-600 hover:underline">
                        {label}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {address || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2">
                      <SubscriberCountPill count={nSubs} subscribers={linked} locationId={id} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <ActionMenu
                        title={`Actions for ${label}`}
                        items={[
                          { label: 'View details', onClick: () => navigate(`/locations/${id}`) },
                          ...(soloSubId
                            ? [{ label: 'View customer', onClick: () => navigate(`/subscribers/${soloSubId}`) }]
                            : nSubs > 1
                              ? [{ label: `View all ${nSubs} customers`, onClick: () => navigate(`/locations/${id}`) }]
                              : []),
                          {
                            label: status === 'inactive' ? 'Reactivate' : 'Mark inactive',
                            onClick: () => statusMutation.mutate({ id, override: status === 'inactive' ? null : 'inactive' }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination {...pagination} />
      </div>
    </div>
  );
}
